import { useState } from "react";
import { holeBet, holePayout, formatMoney } from "@/lib/game";

// A compact 18-hole scorecard.
// Renders as a grid with sticky player column; birdie cells highlighted gold; winner cells highlighted volt.
export default function Scorecard({
    players,
    course,
    holes,
    currentHole,
    onSelectHole,
}) {
    const startingBet = course.startingBet ?? 3;
    const eagleEnabled = course.eagleEnabled ?? true;
    const [selectedPlayerIdx, setSelectedPlayerIdx] = useState(0);

    const formatTotal = (v) => {
        if (v === 0) return "$0";
        const formatted = formatMoney(v);
        return v > 0 ? `+${formatted}` : formatted;
    };

    const n = players.length;
    const runningTotals = [];
    let currentTotal = 0;
    for (let i = 0; i < 18; i++) {
        const h = holes[i];
        const r = h?.result;
        if (r && r.status === "won" && r.winnerIdx != null) {
            const payout = holePayout(i, holes, startingBet);
            const share = payout / (n - 1);
            if (r.winnerIdx === selectedPlayerIdx) {
                currentTotal += payout;
            } else {
                currentTotal -= share;
            }
        }
        runningTotals.push(currentTotal);
    }
    return (
        <div className="overflow-x-auto -mx-4 sm:mx-0" data-testid="scorecard">
            <div className="min-w-[820px]">
                <div className="score-grid">
                    {/* Header rows */}
                    <div className="score-cell head name" style={{ background: "#0f1113" }}>
                        Hole
                    </div>
                    {Array.from({ length: 18 }).map((_, i) => (
                        <button
                            key={`h-${i}`}
                            onClick={() => onSelectHole && onSelectHole(i)}
                            className={`score-cell head`}
                            data-testid={`scorecard-hole-header-${i}`}
                            style={{
                                cursor: onSelectHole ? "pointer" : "default",
                                color:
                                    currentHole === i
                                        ? "var(--volt)"
                                        : undefined,
                                background:
                                    currentHole === i ? "#141719" : undefined,
                            }}
                        >
                            {i + 1}
                        </button>
                    ))}
                    <div className="score-cell head">TOT</div>

                    <div className="score-cell head name">Par</div>
                    {course.pars.map((p, i) => (
                        <div key={`p-${i}`} className="score-cell head">
                            {p}
                        </div>
                    ))}
                    <div className="score-cell head">
                        {course.pars.reduce((a, b) => a + b, 0)}
                    </div>

                    <div className="score-cell head name">Idx</div>
                    {course.holeHandicapRatings.map((r, i) => (
                        <div key={`r-${i}`} className="score-cell head">
                            {r}
                        </div>
                    ))}
                    <div className="score-cell head">—</div>

                    <div className="score-cell head name">Bet</div>
                    {Array.from({ length: 18 }).map((_, i) => (
                        <div
                            key={`b-${i}`}
                            className="score-cell head"
                            style={{ color: "var(--volt)" }}
                        >
                            ${holeBet(i, holes, startingBet)}
                        </div>
                    ))}
                    <div
                        className="score-cell head"
                        style={{ color: "var(--volt)" }}
                    >
                        $
                        {Array.from({ length: 18 })
                            .map((_, i) => holeBet(i, holes, startingBet))
                            .reduce((a, b) => a + b, 0)}
                    </div>

                    {/* Player rows */}
                    {players.map((p, pi) => {
                        let total = 0;
                        return [
                            <div
                                key={`n-${pi}`}
                                className="score-cell name"
                                data-testid={`scorecard-player-name-${pi}`}
                            >
                                {p.name || `P${pi + 1}`}
                            </div>,
                            ...holes.map((h, hi) => {
                                const g = h.gross?.[pi];
                                if (g != null) total += g;
                                const par = course.pars[hi];
                                const isEagle = eagleEnabled && g != null && g <= par - 2;
                                const isBirdie = !isEagle && g === par - 1;
                                const isWinner =
                                    h.result?.status === "won" &&
                                    h.result.winnerIdx === pi;
                                let cls = "score-cell";
                                if (isEagle && isWinner) cls += " eagle";
                                else if (isBirdie && isWinner) cls += " birdie";
                                else if (isWinner) cls += " winner";
                                return (
                                    <div
                                        key={`s-${pi}-${hi}`}
                                        className={cls}
                                        data-testid={`scorecard-cell-${pi}-${hi}`}
                                    >
                                        {g ?? "·"}
                                    </div>
                                );
                            }),
                            <div
                                key={`t-${pi}`}
                                className="score-cell total"
                                data-testid={`scorecard-total-${pi}`}
                            >
                                {total || "—"}
                            </div>,
                        ];
                    })}

                    {/* Player Progress row */}
                    <div className="score-cell name" style={{ background: "#0f1113", overflow: "visible", padding: "0 4px" }}>
                        <select
                            className="bg-transparent text-[11px] font-[600] text-[color:var(--bone)] w-full outline-none cursor-pointer uppercase tracking-wider"
                            value={selectedPlayerIdx}
                            onChange={(e) => setSelectedPlayerIdx(Number(e.target.value))}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="player-progress-select"
                        >
                            {players.map((p, i) => (
                                <option key={i} value={i} style={{ background: "#0f1113" }}>
                                    {p.name || `P${i + 1}`} Prog
                                </option>
                            ))}
                        </select>
                    </div>
                    {holes.map((h, hi) => {
                        const total = runningTotals[hi];
                        const nextBet = hi < 17 ? holeBet(hi + 1, holes, startingBet) : null;
                        return (
                            <div
                                key={`prog-${hi}`}
                                className="score-cell"
                                style={{
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    lineHeight: "1.1",
                                    color: total > 0 ? "var(--volt)" : total < 0 ? "var(--danger)" : "var(--smoke)",
                                }}
                            >
                                <div className="font-mono text-[11px]">{formatTotal(total)}</div>
                                {nextBet != null && (
                                    <div className="text-[8px] uppercase tracking-tighter" style={{ color: "var(--smoke)", marginTop: "2px" }}>
                                        for ${nextBet}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div 
                        className="score-cell total" 
                        style={{ 
                            color: runningTotals[17] > 0 ? "var(--volt)" : runningTotals[17] < 0 ? "var(--danger)" : "var(--bone)",
                            fontSize: "12px"
                        }}
                    >
                        {formatTotal(runningTotals[17])}
                    </div>

                    {/* Pot row */}
                    <div className="score-cell name" style={{ color: "var(--smoke)" }}>
                        Pot
                    </div>
                    {holes.map((h, hi) => {
                        const r = h.result;
                        const p = holePayout(hi, holes, startingBet);
                        const label = r
                            ? r.status === "tied"
                                ? "—"
                                : `$${p}`
                            : "·";
                        return (
                            <div
                                key={`pot-${hi}`}
                                className="score-cell"
                                style={{
                                    color: r?.eagle
                                        ? "var(--eagle)"
                                        : r?.birdie
                                          ? "var(--gold)"
                                          : r?.status === "won"
                                            ? "var(--volt)"
                                            : "var(--smoke)",
                                }}
                            >
                                {label}
                            </div>
                        );
                    })}
                    <div className="score-cell total">
                        $
                        {holes.reduce(
                            (a, _, i) => a + holePayout(i, holes, startingBet),
                            0,
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
